import {Component, Inject, KeyValueDiffers, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {Subject} from 'rxjs';
import {debounceTime, map, startWith, takeUntil} from 'rxjs/operators';
import {HierarchyEntity} from '../../../shared/hierarchy.service';
import {validateNotBlank} from '../../../commons/validators';
import {AccountService, PasswordStrengthComponent, PwdStrengthModel} from 'auth-commons';
import {ConfigurationService} from '../../../shared/configuration.service';
import {EMAIL_PATTERN, MOBILE_PATTERN, NAME_PATTERN, TAX_ID_PATTERN, VAT_NUMBER_PATTERN} from '../../../shared/patterns';
import {
    DEFAULT_MAX_CHARS,
    EMAIL_MAX_CHARS,
    MOBILE_MAX_CHARS,
    NAME_MAX_CHARS,
    TAX_ID_MAX_CHARS,
    VAT_NUMBER_MAX_CHARS
} from '../../../shared/limits';
import {ECRepresentativeAddress, ECRepresentativePersonalData, ECRepresentativeType} from 'smag-commons/cfg/hierarchy';
import {containsForbiddenWords} from '../../../commons/commons';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {TenantInfo} from '../../dev-dashboard/dev-dashboard.component';
import {TenantsAndNetworks} from '../ec-representatives.component';
import {Address} from '../../../shared/google-maps-address-autocomplete/google-maps-address-autocomplete.component';
import {MatOptionSelectionChange} from '@angular/material/core';
import {mkTextField} from '../../../shared/energy-community';
import {mkAddress} from '../../../shared/ec-members';

export interface NewRepresentativeMember {
    memberType: ECRepresentativeType;
    personalData: ECRepresentativePersonalData;
    sendInvitation: boolean;
    enable2fa: boolean;
    password: string;
    networkIds: string[];
    tenant: string;
}

interface CommunitiesData {
    tenantsAndNetworks: TenantsAndNetworks[];
    tenants: TenantInfo[];
    singleTenantId: string;
}

class NewPwdModel implements PwdStrengthModel {
    newPwd?: string;
    repeatedPwd?: string;

    get password(): string {
        return this.newPwd;
    }
}

@Component({
    selector: 'app-new-representative',
    templateUrl: './new-representative.component.html',
    styleUrls: ['./new-representative.component.scss']
})
export class NewRepresentativeComponent extends PasswordStrengthComponent implements OnInit, OnDestroy {

    private destroyed = new Subject<void>();

    readonly freeCommsFilter: FormControl;
    timeZone: string;
    form: FormGroup;
    podEntity: HierarchyEntity;
    assignations: HierarchyEntity[];
    tenantsAndNetworks: TenantsAndNetworks[];
    tenants: TenantInfo[];
    singleTenantId: string;
    selectedCommunity: FormControl;
    newMember: NewRepresentativeMember;
    hidePwd: boolean;
    hideRepeatedPwd: boolean;
    model: NewPwdModel = new NewPwdModel();
    currentAddress: ECRepresentativeAddress;
    freeCommsForTenant: HierarchyEntity[];
    arrSelectableComms: HierarchyEntity[];
    tempC: HierarchyEntity;

    // Exported in HTML
    MOBILE_MAX_CHARS = MOBILE_MAX_CHARS;
    VAT_NUMBER_MAX_CHARS = VAT_NUMBER_MAX_CHARS;
    NAME_MAX_CHARS = NAME_MAX_CHARS;
    EMAIL_MAX_CHARS = EMAIL_MAX_CHARS;
    DEFAULT_MAX_CHARS = DEFAULT_MAX_CHARS;
    TAX_ID_MAX_CHARS = TAX_ID_MAX_CHARS;
    ECRepresentativeType = ECRepresentativeType;

    constructor(@Inject(MAT_DIALOG_DATA) readonly data: CommunitiesData,
                private readonly configurationService: ConfigurationService,
                protected readonly differs: KeyValueDiffers,
                protected readonly accountService: AccountService,
                private fb: FormBuilder,
                private dialogRef: MatDialogRef<NewRepresentativeComponent>) {
        super(differs, accountService);
        this.timeZone = null;
        this.form = this.fb.group({
            tenant: new FormControl(null, []),
            firstName: new FormControl(null, [validateNotBlank, Validators.pattern(NAME_PATTERN)]),
            lastName: new FormControl(null, [validateNotBlank, Validators.pattern(NAME_PATTERN)]),
            email: new FormControl('', [validateNotBlank, Validators.pattern(EMAIL_PATTERN)]),
            confirmEmail: new FormControl('', [validateNotBlank, Validators.pattern(EMAIL_PATTERN)]),
            mobileNumber: new FormControl(null, [validateNotBlank, Validators.pattern(MOBILE_PATTERN)]),
            sendInvitation: new FormControl(false, [Validators.required]),
            password: new FormControl('', [Validators.required]),
            confirmPassword: new FormControl('', [Validators.required]),
            enable2FA: new FormControl(false, [Validators.required]),
            representativeType: new FormControl(ECRepresentativeType.NATURAL_PERSON, [Validators.required]),
            companyName: new FormControl(null, []),
            taxId: new FormControl(null, [Validators.pattern(TAX_ID_PATTERN)]),
            vatNumber: new FormControl(null, [Validators.pattern(VAT_NUMBER_PATTERN)]),
            address: new FormControl(null, []),
            telephone: new FormControl(null, [Validators.pattern(MOBILE_PATTERN)]),
            pec: new FormControl(null, [Validators.pattern(EMAIL_PATTERN)])
        }, {
            validators: [validateForm]
        });
        this.podEntity = null;
        this.assignations = [];
        this.tenantsAndNetworks = data.tenantsAndNetworks;
        this.tenants = data.tenants;
        this.singleTenantId = data.singleTenantId;
        this.selectedCommunity = new FormControl();
        this.newMember = null;
        this.hidePwd = true;
        this.hideRepeatedPwd = true;
        this.model.newPwd = '';
        this.freeCommsForTenant = [];
        this.arrSelectableComms = [];
        this.tempC = null;
        this.freeCommsFilter = new FormControl();
    }

    ngOnInit() {
        super.ngOnInit();
        this.form.valueChanges.pipe(
            debounceTime(250),
            takeUntil(this.destroyed)
        ).subscribe(value => {
            // Manage pwd strength fields
            this.model.newPwd = value.password;
            this.model.repeatedPwd = value.confirmPassword;
            // Update internal representative
            const email = mkTextField(value.email);
            const pec = mkTextField(value.pec);
            this.newMember = {
                memberType: value.representativeType,
                personalData: {
                    email: email ? email.toLowerCase() : null,
                    company_name: value.representativeType !== ECRepresentativeType.NATURAL_PERSON ? mkTextField(value.companyName) : undefined,
                    first_name: mkTextField(value.firstName),
                    last_name: mkTextField(value.lastName),
                    cf: mkTextField(value.taxId),
                    piva: mkTextField(value.vatNumber),
                    pec: pec ? pec.toLowerCase() : null,
                    address: this.currentAddress ? {
                        street: this.currentAddress?.street,
                        street_number: this.currentAddress?.street_number,
                        postal_code: this.currentAddress?.postal_code,
                        city: this.currentAddress?.city,
                        province: this.currentAddress?.province
                    } as ECRepresentativeAddress : null,
                    mobile_number: mkTextField(value.mobileNumber),
                    telephone_number: mkTextField(value.telephone)
                } as ECRepresentativePersonalData,
                sendInvitation: value.sendInvitation,
                enable2fa: value.enable2FA,
                password: mkTextField(value.password),
                networkIds: null,
                tenant: value.tenant?.id || this.singleTenantId
            } as NewRepresentativeMember;
        });
        this.configurationService.timeConfig.pipe(
            takeUntil(this.destroyed)
        ).subscribe(timeCfg => {
            this.timeZone = timeCfg.time_zone;
        });
        if (this.tenantsAndNetworks.length === 1) {
            this.freeCommsForTenant = this.tenantsAndNetworks[0].freeNetworks;
        }
        this.freeCommsFilter.valueChanges.pipe(
            startWith(''),
            map(value => {
                if (!value || value.length === 0) {
                    this.tempC = null;
                }
                return this.filterCommsByName(value);
            }),
        ).subscribe(d => {
            this.arrSelectableComms = d;
        });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get uniqueEmailError(): boolean {
        return this.form.controls.email.hasError('unique_email');
    }

    get notMatchingEmails(): boolean {
        return this.form.controls.confirmEmail.dirty && this.form.controls.confirmEmail.hasError('not_matching_emails');
    }

    get notMatchingPasswords(): boolean {
        return this.form.controls.confirmPassword.dirty && this.form.controls.confirmPassword.hasError('not_matching_passwords');
    }

    get pwContainsForbiddenWords(): boolean {
        return this.form.controls.password.dirty && this.form.controls.password.hasError('forbidden_words');
    }

    get addressFormControl(): FormControl {
        return this.form.controls.address as FormControl;
    }

    get isFirstPageValid(): boolean {
        return this.form.controls.firstName.valid &&
            this.form.controls.lastName.valid &&
            this.form.controls.email.valid &&
            this.form.controls.confirmEmail.valid &&
            this.form.controls.mobileNumber.valid &&
            this.form.controls.sendInvitation.valid &&
            this.form.controls.password.valid &&
            this.form.controls.confirmPassword.valid &&
            this.form.controls.enable2FA.valid;
    }

    get isSecondPageValid(): boolean {
        let valid = this.form.controls.representativeType.valid &&
            this.form.controls.address.valid &&
            this.form.controls.telephone.valid &&
            this.form.controls.pec.valid;
        if (this.form.controls.representativeType.value === ECRepresentativeType.PRIVATE_INSTITUTION ||
            this.form.controls.representativeType.value === ECRepresentativeType.PUBLIC_INSTITUTION) {
            valid = valid && this.form.controls.companyName.valid &&
                (this.form.controls.companyName.value?.length > 0) && this.form.controls.vatNumber.valid;
        } else {
            valid = valid && this.form.controls.taxId.valid;
        }
        return valid;
    }

    sendRepresentative(useAssignments: boolean) {
        // Enrich the newMember data with non-form-dependant values
        if (this.newMember) {
            this.newMember.networkIds = useAssignments ? this.assignations.map(assignation => assignation.id) : [];
        }
        this.dialogRef.close(this.newMember);
    }

    isControlOK(controlName: string): boolean {
        return this.form.controls[controlName].dirty && !this.form.controls[controlName].valid;
    }

    formFullName(form: FormGroup): string {
        return form.controls.firstName.value + ' ' + form.controls.lastName.value;
    }

    onSelectTenant(event: any) {
        const currentTenant = event.source.value;
        if (currentTenant) {  // super-admin
            this.freeCommsForTenant = this.tenantsAndNetworks.filter(n => n.tenantId === currentTenant.id || (!n.tenantId && !currentTenant.id))[0].freeNetworks;
        } else {  // supervisor or single tenant env, t only contains one element
            this.freeCommsForTenant = this.tenantsAndNetworks[0].freeNetworks;
        }
        this.freeCommsFilter.setValue('');  // re-trigger changed in available communities
    }

    onSelectCommunity(comm: MatOptionSelectionChange) {
        this.tempC = this.arrSelectableComms.filter(a => a.name === comm.source.value)[0];
    }

    onAddCommunity(tenant: TenantInfo) {
        this.assignations.push(this.tempC);
        // If tenant exists, we are in the multi-tenant scenario
        const obj = tenant ? this.tenantsAndNetworks.filter(t => t.tenantId === tenant.id)[0] : this.tenantsAndNetworks[0];
        obj.freeNetworks = obj.freeNetworks.filter(n => n.id !== this.tempC.id);
        this.freeCommsForTenant = this.freeCommsForTenant.filter(c => c.id !== this.tempC.id);
        this.freeCommsFilter.setValue('');
        this.tempC = null;
    }

    removeAssignation(ass: HierarchyEntity, tenant: TenantInfo) {
        if (tenant) {  // multi-tenant scenario
            this.tenantsAndNetworks.filter(t => t.tenantId === tenant.id)[0].freeNetworks.push(ass);
        } else {  // only one selectable tenant
            this.tenantsAndNetworks[0].freeNetworks.push(ass);
        }
        this.freeCommsForTenant.push(ass);
        this.assignations = this.assignations.filter(assignation => assignation.id !== ass.id);
        this.freeCommsFilter.setValue('');
        this.tempC = null;
    }

    addressChange(event: Address | null) {
        this.currentAddress = mkAddress(event);
        this.addressFormControl.setValue(this.currentAddress);
    }

    sortByName = (a, b) => {
        return a.value.name > b.value.name ? 1 : -1;
    }

    private filterCommsByName(text: string): HierarchyEntity[] {
        if (typeof text === 'string') {
            return this.freeCommsForTenant.filter(c => c.name.toLowerCase().includes(text.toLowerCase()));
        }
        return this.freeCommsForTenant;
    }
}


function validateForm(ctrl: AbstractControl): ValidationErrors | null {
    const errs: ValidationErrors = {};
    const ctrls: { [ctrl: string]: AbstractControl } = (ctrl as FormGroup).controls;
    if (mkTextField(ctrls.email.value) !== mkTextField(ctrls.confirmEmail.value)) {
        ctrls.confirmEmail.setErrors({not_matching_emails: true});
    } else {
        ctrls.confirmEmail.setErrors(null);
    }
    if (ctrls.password.value !== ctrls.confirmPassword.value) {
        ctrls.confirmPassword.setErrors({not_matching_passwords: true});
    } else {
        ctrls.confirmPassword.setErrors(null);
    }
    if (ctrls.password.value) {
        if (containsForbiddenWords(ctrls.password.value, [ctrls.firstName.value, ctrls.lastName.value, ctrls.email.value])) {
            ctrls.password.setErrors({forbidden_words: true});
        }
    }
    return Object.values(errs).every(err => !err) ? null : errs;
}

